import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { ApiUrl } from "../Constants/Api";
import LOCAL_KEYS, {
  parseLocalStorageJSON,
  removeFromLocalStorage,
  setToLocalStorage,
} from "./Localstorage";

export const INACTIVITY_LIMIT_MS = 60 * 60 * 1000;
export const WARNING_LEAD_MS = 2 * 60 * 1000;
export const WARNING_AT_MS = INACTIVITY_LIMIT_MS - WARNING_LEAD_MS;
export const SESSION_LAST_ACTIVITY_KEY = "sessionLastActivity";
export const SESSION_USER_KEY = "sessionActivityUser";
export const SESSION_LOGOUT_KEY = "sessionLogout";

const ACTIVITY_EVENTS = ["mousedown", "keydown", "touchstart", "click"];
const KEEPALIVE_GAP_MS = 60 * 1000;
const ACTIVITY_WRITE_GAP_MS = 1000;

let sessionEnding = false;

export function isAuthPath(path) {
  const value = (path || "").toLowerCase();
  return value === "/login" || value.startsWith("/login/") || value.startsWith("/resetpassword");
}

export function formatCountdown(ms) {
  const totalSeconds = Math.max(0, Math.ceil(Number(ms) / 1000) || 0);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export function getSessionPhase(lastActivity, now = Date.now()) {
  const last = Number(lastActivity);
  if (!Number.isFinite(last) || last <= 0) {
    return { phase: "active", remainingMs: INACTIVITY_LIMIT_MS, elapsedMs: 0 };
  }
  const elapsedMs = Math.max(0, now - last);
  const remainingMs = Math.max(0, INACTIVITY_LIMIT_MS - elapsedMs);
  if (elapsedMs >= INACTIVITY_LIMIT_MS) {
    return { phase: "expired", remainingMs: 0, elapsedMs };
  }
  if (elapsedMs >= WARNING_AT_MS) {
    return { phase: "warning", remainingMs, elapsedMs };
  }
  return { phase: "active", remainingMs, elapsedMs };
}

export function shouldResetInactivityTimer(checkResult) {
  return checkResult === "valid";
}

export function shouldEndSession(checkResult) {
  return checkResult === "invalid";
}

export function syncActivityClock(storage, userId, now) {
  if (!storage || !userId) return null;
  const bound = storage.getItem(SESSION_USER_KEY);
  const stamp = Number(storage.getItem(SESSION_LAST_ACTIVITY_KEY));
  if (bound !== String(userId) || !Number.isFinite(stamp) || stamp <= 0) {
    storage.setItem(SESSION_USER_KEY, String(userId));
    storage.setItem(SESSION_LAST_ACTIVITY_KEY, String(now));
    return now;
  }
  return stamp;
}

export function broadcastSessionLogout() {
  try {
    localStorage.setItem(SESSION_LOGOUT_KEY, String(Date.now()));
  } catch (error) {
    // Ignore storage failures. The current tab still clears its own session.
  }
}

export function endSession() {
  if (sessionEnding) return;
  sessionEnding = true;
  try {
    broadcastSessionLogout();
    removeFromLocalStorage();
    const path = window.location.pathname || "";
    if (!isAuthPath(path)) {
      window.location.replace("/login");
    }
  } finally {
    sessionEnding = false;
  }
}

function readAccessToken() {
  try {
    return parseLocalStorageJSON(LOCAL_KEYS.ACCESS_TOKEN);
  } catch (error) {
    return null;
  }
}

function readUserId() {
  try {
    const user = parseLocalStorageJSON(LOCAL_KEYS.USER_DETAILS);
    if (!user || typeof user !== "object") return "signed-in";
    return String(user.id || user.userName || user.email || "signed-in");
  } catch (error) {
    return "signed-in";
  }
}

export async function confirmSessionStillValid() {
  const accessToken = readAccessToken();
  if (!accessToken) return "invalid";
  const refreshToken = parseLocalStorageJSON(LOCAL_KEYS.REFRESH_TOKEN);

  let keepAliveStatus;
  try {
    const keepAlive = await axios.post(
      ApiUrl.sessionKeepAlive(),
      {},
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        timeout: 15000,
        validateStatus: () => true,
      }
    );
    keepAliveStatus = keepAlive.status;
  } catch (error) {
    return "unavailable";
  }

  if (keepAliveStatus === 200) return "valid";
  if (keepAliveStatus !== 401 && keepAliveStatus !== 403) return "unavailable";
  if (!refreshToken || typeof ApiUrl.refreshToken !== "function") return "invalid";

  try {
    const refreshed = await axios.post(
      ApiUrl.refreshToken(),
      { refreshToken },
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        timeout: 15000,
        validateStatus: () => true,
      }
    );
    if (!refreshed || refreshed.status >= 500 || refreshed.status === 0) return "unavailable";
    const nextAccess = refreshed.data && refreshed.data.access_token;
    const nextRefresh = refreshed.data && (refreshed.data.refresh_token || refreshToken);
    if (refreshed.status === 200 && nextAccess) {
      setToLocalStorage(LOCAL_KEYS.ACCESS_TOKEN, nextAccess);
      if (nextRefresh) setToLocalStorage(LOCAL_KEYS.REFRESH_TOKEN, nextRefresh);
      return "valid";
    }
    if (refreshed.status === 200) return "unavailable";
    if (refreshed.status === 401 || refreshed.status === 403) return "invalid";
    return "unavailable";
  } catch (error) {
    return "unavailable";
  }
}

export function useIdleSession() {
  const location = useLocation();
  const onAuthScreen = isAuthPath(location.pathname);
  const [open, setOpen] = useState(false);
  const [remainingMs, setRemainingMs] = useState(WARNING_LEAD_MS);
  const [stayPending, setStayPending] = useState(false);
  const [stayMessage, setStayMessage] = useState("");
  const warningRef = useRef(false);
  const dismissedRef = useRef(false);
  const stayPendingRef = useRef(false);
  const lastWriteRef = useRef(0);
  const lastKeepAliveRef = useRef(0);

  const tick = useCallback(() => {
    if (onAuthScreen || !readAccessToken()) {
      warningRef.current = false;
      setOpen(false);
      return;
    }
    const stamp = syncActivityClock(window.localStorage, readUserId(), Date.now());
    const next = getSessionPhase(stamp, Date.now());
    if (next.phase === "expired") {
      endSession();
      return;
    }
    if (next.phase === "active") {
      dismissedRef.current = false;
    }
    warningRef.current = next.phase === "warning";
    setOpen(next.phase === "warning" && !dismissedRef.current);
    setRemainingMs(next.remainingMs);
  }, [onAuthScreen]);

  useEffect(() => {
    tick();
    const intervalId = window.setInterval(tick, 1000);
    const onStorage = (event) => {
      if (event.key === SESSION_LOGOUT_KEY && event.newValue) {
        endSession();
        return;
      }
      if (event.key === SESSION_LAST_ACTIVITY_KEY || event.key === LOCAL_KEYS.ACCESS_TOKEN) {
        tick();
      }
    };
    const onActivity = () => {
      if (onAuthScreen || !readAccessToken() || warningRef.current || stayPendingRef.current) return;
      const now = Date.now();
      if (now - lastWriteRef.current >= ACTIVITY_WRITE_GAP_MS) {
        lastWriteRef.current = now;
        window.localStorage.setItem(SESSION_LAST_ACTIVITY_KEY, String(now));
      }
      if (now - lastKeepAliveRef.current >= KEEPALIVE_GAP_MS) {
        lastKeepAliveRef.current = now;
        confirmSessionStillValid().then((result) => {
          if (shouldEndSession(result)) endSession();
        });
      }
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
    };
    ACTIVITY_EVENTS.forEach((name) => window.addEventListener(name, onActivity, { passive: true }));
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(intervalId);
      ACTIVITY_EVENTS.forEach((name) => window.removeEventListener(name, onActivity));
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [onAuthScreen, tick]);

  const onStay = async () => {
    if (stayPendingRef.current) return;
    stayPendingRef.current = true;
    setStayPending(true);
    setStayMessage("");
    const result = await confirmSessionStillValid();
    stayPendingRef.current = false;
    setStayPending(false);
    if (shouldResetInactivityTimer(result)) {
      const now = Date.now();
      window.localStorage.setItem(SESSION_LAST_ACTIVITY_KEY, String(now));
      lastWriteRef.current = now;
      warningRef.current = false;
      setOpen(false);
      setStayMessage("");
      return;
    }
    if (shouldEndSession(result)) {
      endSession();
      return;
    }
    setStayMessage("We could not verify your session. You can try again before the timer ends.");
  };

  const onDismiss = () => {
    dismissedRef.current = true;
    setOpen(false);
  };

  const onLogout = () => endSession();

  return {
    open,
    countdown: formatCountdown(remainingMs),
    stayPending,
    stayMessage,
    onStay,
    onDismiss,
    onLogout,
  };
}
